import { NextUIProvider } from '@nextui-org/react'
import { AnimatePresence } from 'framer-motion'
import { FC } from 'react'
import { useSelector } from 'react-redux'
import { Route, Routes, useNavigate } from 'react-router-dom'
import Layout from './components/Layout/Layout'
import LoadSeller from './components/LoadSeller/LoadSeller'
import { PrivateRoutes, PublicRoutes } from './constants/routes'
import AuthRoleRouterGuard from './guards/AuthRoleRouterGuard'
import SlugRouterGuard from './guards/SlugRouterGuard'
import { useVersionCheck } from './hooks/useVersionCheck'
import { IConfigSeller } from './models/configSeller'
import BulkSamsungPage from './pages/admin/BulkSamsungPage/BulkSamsungPage'
import MailingPage from './pages/admin/MailingPage/MailingPage'
import DataBankPage from './pages/backoffice/DataBankPage/DataBankPage'
import HomePage from './pages/backoffice/HomePage/HomePage'
import ListPage from './pages/backoffice/ListPage/ListPage'
import LoginPage from './pages/backoffice/LoginPage/LoginPage'
import NotFoundPage from './pages/backoffice/NotFoundPage/NotFoundPage'
import PickupForm from './pages/backoffice/PickupForm/PickupForm'
import TradeInPage from './pages/backoffice/TradeInPage/TradeInPage'
import UploadPrices from './pages/backoffice/UploadPrices/UploadPrices'
import BankAccountPage from './pages/cliente/BankAccountPage/BankAccountPage'
import ContractPage from './pages/cliente/ContractPage/ContractPage'
import { HomePageProvider } from './pages/cliente/FormularioClientePage/context/FormularioClienteContext'
import FormularioClientePage from './pages/cliente/FormularioClientePage/FormularioClientePage'
import SuccessPage from './pages/cliente/SuccessPage/SuccessPage'
import './theme/App.css'
import VersionCheckPage from './pages/backoffice/VersionCheck/VersionCheckPage'
import ContributionsPage from './pages/admin/ContributionsPage/ContributionsPage'
import ContributionFormPage from './pages/admin/ContributionFormPage/ContributionFormPage'
import { FormCampaignProvider } from './pages/admin/ContributionFormPage/context/FormCampaignContext'

const App: FC = () => {
  const navigate = useNavigate()
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)

  const updateAvailable = useVersionCheck()

  if (updateAvailable) {
    console.log('🚀 ~ updateAvailable:', updateAvailable)
    return <VersionCheckPage />
  }

  return (
    <NextUIProvider navigate={navigate}>
      <AnimatePresence mode='wait'>
        <Routes>
          <Route path={PublicRoutes.NOTFOUND} element={<NotFoundPage />} />
          <Route path={PublicRoutes.ROOT} element={<LoadSeller />}>
            <Route path={PublicRoutes.CONTRACT} element={<ContractPage />} />
            <Route path={PublicRoutes.ROOT} element={<h1>Es necesario ingresar un slug</h1>} />
            {
                  configSeller.config && configSeller.config.s3.FOLDER_APP !== 'ti_macstore' && configSeller.config.s3.FOLDER_APP !== 'ti_coppel'
                    ? (
                      <>
                        <Route path={PrivateRoutes.PICKUP_FORM} element={<PickupForm />} />
                      </>
                      )
                    : null
                }
            <Route element={<SlugRouterGuard sellerSlug='samsung' />}>
              <Route path={PublicRoutes.BANKACCOUNT} element={<BankAccountPage />} />
            </Route>
            <Route path={PublicRoutes.LOGIN} element={<LoginPage />} />

            <Route element={<Layout />}>
              <Route element={<AuthRoleRouterGuard roles={[Number(import.meta.env.VITE_PERFIL_SOLOLECTURA), Number(import.meta.env.VITE_PERFIL_CUSTODIA), Number(import.meta.env.VITE_PERFIL_ADMIN), Number(import.meta.env.VITE_PERFIL_EJECUTIVO), Number(import.meta.env.VITE_PERFIL_EJECUTIVO_CAJA), Number(import.meta.env.VITE_PERFIL_JEFETIENDA), Number(import.meta.env.VITE_PERFIL_COURIER), Number(import.meta.env.VITE_PERFIL_ADMIN_SELLER), Number(import.meta.env.VITE_PERFIL_TI_COTIZADOR)]} />}>
                <Route path={PrivateRoutes.HOME + '*'} element={<HomePage />} />
              </Route>

              <Route element={<AuthRoleRouterGuard roles={[Number(import.meta.env.VITE_PERFIL_SOLOLECTURA), Number(import.meta.env.VITE_PERFIL_ADMIN), Number(import.meta.env.VITE_PERFIL_EJECUTIVO), Number(import.meta.env.VITE_PERFIL_EJECUTIVO_CAJA), Number(import.meta.env.VITE_PERFIL_JEFETIENDA), Number(import.meta.env.VITE_PERFIL_COURIER), Number(import.meta.env.VITE_PERFIL_ADMIN_SELLER), Number(import.meta.env.VITE_PERFIL_TI_COTIZADOR)]} />}>
                <Route path={PrivateRoutes.TRADEIN} element={<TradeInPage />} />
              </Route>
              <Route element={<AuthRoleRouterGuard roles={[Number(import.meta.env.VITE_PERFIL_SOLOLECTURA), Number(import.meta.env.VITE_PERFIL_ADMIN), Number(import.meta.env.VITE_PERFIL_EJECUTIVO), Number(import.meta.env.VITE_PERFIL_EJECUTIVO_CAJA), Number(import.meta.env.VITE_PERFIL_JEFETIENDA), Number(import.meta.env.VITE_PERFIL_COURIER), Number(import.meta.env.VITE_PERFIL_ADMIN_SELLER)]} />}>
                <Route path={PrivateRoutes.PICKUP_FORM} element={<PickupForm />} />
              </Route>

              <Route element={<AuthRoleRouterGuard roles={[Number(import.meta.env.VITE_PERFIL_ADMIN), Number(import.meta.env.VITE_PERFIL_ADMIN_SELLER)]} />}>
                <Route element={<SlugRouterGuard sellerSlug='falabella' />}>
                  <Route path={PrivateRoutes.MAILING} element={<MailingPage />} />
                </Route>
                <Route element={<SlugRouterGuard sellerSlug='samsung' />}>
                  <Route path={PrivateRoutes.LOADSAMSUNGDATA} element={<BulkSamsungPage />} />
                  <Route path={PrivateRoutes.DATABANK} element={<DataBankPage />} />
                </Route>
              </Route>
              <Route element={<AuthRoleRouterGuard roles={[Number(import.meta.env.VITE_PERFIL_ADMIN)]} />}>
                <Route path={PrivateRoutes.CONTRIBUTIONS} element={<ContributionsPage />} />
                <Route path={PrivateRoutes.CONTRIBUTION} element={<FormCampaignProvider><ContributionFormPage /></FormCampaignProvider>} />
              </Route>
              <Route element={<AuthRoleRouterGuard roles={[Number(import.meta.env.VITE_PERFIL_ADMIN)]} />}>
                <Route path={PrivateRoutes.SUBIR_PRECIOS} element={<UploadPrices />} />
              </Route>
              <Route element={<AuthRoleRouterGuard roles={[Number(import.meta.env.VITE_PERFIL_TECHNICIANS)]} />}>
                <Route path={PrivateRoutes.LISTADO_TECNICOS} element={<ListPage />} />
              </Route>
              <Route element={<AuthRoleRouterGuard roles={[Number(import.meta.env.VITE_PERFIL_SOLOLECTURA), Number(import.meta.env.VITE_PERFIL_CUSTODIA), Number(import.meta.env.VITE_PERFIL_ADMIN), Number(import.meta.env.VITE_PERFIL_EJECUTIVO), Number(import.meta.env.VITE_PERFIL_EJECUTIVO_CAJA), Number(import.meta.env.VITE_PERFIL_JEFETIENDA), Number(import.meta.env.VITE_PERFIL_COURIER), Number(import.meta.env.VITE_PERFIL_ADMIN_SELLER), Number(import.meta.env.VITE_PERFIL_TI_COTIZADOR)]} />}>
                <Route path={PrivateRoutes.FORMULARIO} element={<HomePageProvider><FormularioClientePage /></HomePageProvider>} />
                <Route path={PrivateRoutes.SUCCESS} element={<SuccessPage />} />
              </Route>
            </Route>
            <Route path={PublicRoutes.SLUG} element={<HomePageProvider><FormularioClientePage /></HomePageProvider>} />
            <Route path={PublicRoutes.SUCCESS} element={<SuccessPage />} />
            <Route path={PrivateRoutes.HOME + '*'} element={<NotFoundPage />} />
          </Route>
        </Routes>
      </AnimatePresence>
    </NextUIProvider>
  )
}

export default App
